import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress } from '@mui/material';
import { NUMBER } from '../../../constants/app-constants'; // Ensure this constant exists
import { pad } from '../../../utils/helpers/secondsToHMS'; // Import your pad utility

const Timer = ({ 
    interval, 
    clsName = '', 
    onComplete = () => {}, 
    onlyText = false, 
    isCountdownTimer = false 
}) => {
    const [countdownInterval, setCountdownInterval] = useState(interval);
    const intervalRef = useRef(null);

    useEffect(() => {
        // Start the countdown
        const startCountdown = () => {
            intervalRef.current = setInterval(() => {
                setCountdownInterval((prev) => {
                    if (prev <= 1) {
                        // Timer completed
                        onComplete();
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;

                        // Reset if isCountdownTimer flag is true
                        return isCountdownTimer ? NUMBER.SIXTY : 0;
                    }
                    return prev - 1; // Decrement countdown
                });
            }, NUMBER.THOUSAND); // Update every 1000 ms
        };

        startCountdown();

        // Cleanup interval on unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [interval, isCountdownTimer, onComplete]);

    // Calculate progress for CircularProgress
    const progress = Math.round((NUMBER.HUNDRED / interval) * countdownInterval);

    // Format the countdown interval using the `pad` function
    const formattedCountdown = pad(countdownInterval);

    return progress > 0 ? (
        !onlyText ? (
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                    variant="determinate"
                    color="secondary"
                    size={20}
                    thickness={4}
                    className="text-xs h-6 w-6"
                    value={progress}
                />
                <Box
                    sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography variant="caption" className={`text-xs ${clsName}`} component="div" color="secondary">
                        {formattedCountdown} {/* Display formatted time */}
                    </Typography>
                </Box>
            </Box>
        ) : (
            <Typography variant="caption" className={`text-xs ${clsName}`} component="div" color="secondary">
                {formattedCountdown} {/* Display formatted time */}
            </Typography>
        )
    ) : null; // If progress is zero, render nothing
};

Timer.propTypes = {
    interval: PropTypes.number.isRequired,
    clsName: PropTypes.string,
    onlyText: PropTypes.bool,
    isCountdownTimer: PropTypes.bool,
    onComplete: PropTypes.func,
};

export default Timer;
